import appSettings from 'wanderu-config';
import { googleDataSelectors } from 'wanderu-ducks-google';
import { searchResultsActions } from 'wanderu-ducks-psearch';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import { fetchSearchPlacesAction } from '../../actions/fetchSearchPlaces';
import { RETURN_DIRECTION } from '../../constants';
import { actions as filterActions } from '../filters';
import { actions as tripSummaryActions } from '../tripSummary';
import { selectors as tripSelectors } from '../trips';
import * as searchSelectors from './selectors';
import * as types from './types';

const logger = Logger.getLogger('search-duck-actions');

function removeSearchParams() {
    return {
        type: types.REMOVE_SEARCH_PARAMS,
    };
}

function _setSearchParams(searchParams) {
    return {
        payload: searchParams,
        type: types.SET_SEARCH_PARAMS,
    };
}

function setQueryParams(queryParams) {
    return {
        payload: queryParams,
        type: types.SET_QUERY_PARAMS,
    };
}

function _setInputParams(inputParams) {
    return {
        payload: inputParams,
        type: types.SET_INPUT_PARAMS,
    };
}

function setSearchParams(newParams, queryParams) {
    return async (dispatch, getState) => {
        let {
            departDate,
            returnDate,
            direction,
            originText,
            originPlaceId,
            passengers,
            destinationText,
            destinationPlaceId,
            departTripId,
            locale,
        } = newParams;

        const oldParams = searchSelectors.selectReduxSearchParams(getState());
        const returnTripId = searchSelectors.selectReturnTripId(getState());

        const sameDirection = direction === oldParams.direction;
        const sameDepartDate = departDate === oldParams.departDate;
        const sameReturnDate = returnDate === oldParams.returnDate;
        const sameOrigin = originPlaceId === oldParams.originPlaceId;
        const sameDestination = destinationPlaceId === oldParams.destinationPlaceId;
        const sameDepartTripId = departTripId === oldParams.departTripId;
        const samePassengers = passengers === oldParams.passengers;
        const allSame = sameDirection && sameDepartDate && sameReturnDate
            && sameOrigin && sameDestination && sameDepartTripId && samePassengers;

        if (allSame) {
            return;
        }

        let originPlace = {};
        let destinationPlace = {};
        let userOriginInput;
        let userDestinationInput;
        let userPassengersInput;
        try {
            const places = await dispatch(fetchSearchPlacesAction.fetch({
                originInput: originText,
                originPlaceId,
                destinationInput: destinationText,
                destinationPlaceId,
                locale,
            }));

            originPlaceId = places.originPlaceId;
            originPlace = googleDataSelectors
                .selectPlaceDetails(getState(), originPlaceId) || {};
            userOriginInput = searchSelectors
                .selectUserInputForPlaceId(getState(), originPlaceId);
            destinationPlaceId = places.destinationPlaceId;
            destinationPlace = googleDataSelectors
                .selectPlaceDetails(getState(), destinationPlaceId) || {};
            userDestinationInput = searchSelectors
                .selectUserInputForPlaceId(getState(), destinationPlaceId);
            userPassengersInput = searchSelectors.selectPassengersUserInput(getState());
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('setSearchParams', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
        }

        const searchParams = {
            departDate,
            returnDate,
            direction,
            originPlaceId,
            destinationPlaceId,
            departTripId,
            returnTripId,
            passengers,
        };

        const inputParams = {
            originPlaceId,
            destinationPlaceId,
            origin: userOriginInput || originPlace.formatted_address,
            destination: userDestinationInput || destinationPlace.formatted_address,
            passengers: passengers || userPassengersInput,
        };

        dispatch(_setSearchParams(searchParams));
        dispatch(_setInputParams(inputParams));
        dispatch(setQueryParams(queryParams));
        dispatch(filterActions.setNewFilterIds(searchParams));

        const parsedPassengers = parseInt(passengers, 10);
        dispatch(tripSummaryActions.setTravelerInfo({
            seats: parsedPassengers > 0
                ? parsedPassengers
                : 1,
        }));
    };
}

function initSearchAndFilters(searchParams, filterParams) {
    return async (dispatch) => {
        await dispatch(setSearchParams(searchParams, filterParams));
        dispatch(filterActions.setFilterParams(filterParams, searchParams));

        if (searchParams.returnDate) {
            dispatch(filterActions.setFilterParams(
                filterParams,
                {
                    ...searchParams,
                    direction: RETURN_DIRECTION,
                },
            ));
        }
    };
}

function setDirection(direction) {
    return {
        payload: direction,
        type: types.SET_DIRECTION,
    };
}

function _setDepartTripId(tripId) {
    return {
        type: types.SET_DEPART_TRIP_ID,
        meta: tripId,
    };
}

function setDepartTripId(tripId) {
    return (dispatch, getState) => {
        const returnTripId = searchSelectors.selectReturnTripId(getState());
        if (!returnTripId) {
            dispatch(_setDepartTripId(tripId));
            return;
        }

        dispatch(_setDepartTripId(tripId));

        const returnTripIsValid = tripSelectors.selectTripSatisfiesFilters(
            getState(),
            returnTripId,
        );
        if (returnTripIsValid) {
            dispatch(_setDepartTripId(tripId));
            return;
        }

        dispatch(removeReturnTripId());
    };
}

function setReturnTripId(tripId) {
    return {
        type: types.SET_RETURN_TRIP_ID,
        meta: tripId,
    };
}

function removeDepartTripId() {
    return {
        type: types.REMOVE_DEPART_TRIP_ID,
    };
}

function removeReturnTripId() {
    return {
        type: types.REMOVE_RETURN_TRIP_ID,
    };
}

function clearSearchResults(searchParams) {
    return (dispatch, getState) => {
        const psearchParams = searchSelectors.selectPSearchParams(getState(), searchParams);
        dispatch(searchResultsActions.clearSearchResults(psearchParams));
    };
}

function setOriginUserInputForPlaceId(input, placeId) {
    return (dispatch) => {
        dispatch(setOriginUserInput(input));
        dispatch(_setUserPlaceId(placeId, input, 'origin'));
    };
}

function setDestinationUserInputForPlaceId(input, placeId) {
    return (dispatch) => {
        dispatch(setDestinationUserInput(input));
        dispatch(_setUserPlaceId(placeId, input, 'destination'));
    };
}

function _setUserPlaceId(placeId, input, direction) {
    return {
        type: types.SET_USER_PLACE_ID,
        meta: { direction },
        payload: { placeId, input },
    };
}

function setOriginUserInput(input) {
    return {
        type: types.SET_ORIGIN_USER_INPUT,
        payload: input,
    };
}

function setPassengersInput(input) {
    return {
        type: types.SET_PASSENGERS_INPUT,
        payload: input,
    };
}

function setDestinationUserInput(input) {
    return {
        type: types.SET_DESTINATION_USER_INPUT,
        payload: input,
    };
}

function setServerBatchId(serverBatchId) {
    return {
        type: types.SET_SERVER_BATCH_ID,
        meta: serverBatchId,
    };
}

export {
    clearSearchResults,
    initSearchAndFilters,
    removeDepartTripId,
    removeReturnTripId,
    removeSearchParams,
    setDepartTripId,
    setDestinationUserInput,
    setDestinationUserInputForPlaceId,
    setDirection,
    setOriginUserInput,
    setOriginUserInputForPlaceId,
    setPassengersInput,
    setQueryParams,
    setReturnTripId,
    setSearchParams,
    setServerBatchId,
};
